import React from 'react';
import {
    Box,
    Drawer,
    IconButton,
    Typography,
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';
import { Link } from 'react-router-dom';
import Assets from './Assets';
import {
    PAGE_MUI_ICONS,
    getAdminPath,
    getIconPath,
} from '../../Utils/adminNavigation';

const DRAWER_WIDTH = 272;

const AdminMobileSidebar = ({
    open,
    onClose,
    groupedPages,
    sectionOrder,
    sectionLabels,
    activePage,
    onNavigate,
    onLogout,
}) => {
    return (
        <Drawer
            anchor="left"
            open={open}
            onClose={onClose}
            variant="temporary"
            ModalProps={{ keepMounted: true }}
            sx={{
                display: { xs: 'block', sm: 'none' },
                zIndex: (theme) => theme.zIndex.drawer + 2,
                '& .MuiDrawer-paper': {
                    width: DRAWER_WIDTH,
                    maxWidth: '82vw',
                    display: 'flex',
                    flexDirection: 'column',
                    boxSizing: 'border-box',
                    backgroundColor: '#ffffff',
                },
            }}
        >
            <Box
                sx={{
                    flexShrink: 0,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 1,
                    px: 2,
                    py: 1,
                    minHeight: 52,
                    borderBottom: '1px solid #e2e8f0',
                }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                    <Assets src="images/logo.svg" height="32px" width="32px" />
                    <Typography
                        noWrap
                        sx={{ fontSize: '18px', fontWeight: 700, color: '#2f2481' }}
                    >
                        PPSK
                    </Typography>
                </Box>
                <IconButton
                    onClick={onClose}
                    size="small"
                    aria-label="Close menu"
                    sx={{
                        backgroundColor: '#f1f5f9',
                        '&:hover': { backgroundColor: '#e2e8f0' },
                    }}
                >
                    <ChevronLeftIcon sx={{ color: '#64748b', fontSize: 20 }} />
                </IconButton>
            </Box>

            <Box
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    WebkitOverflowScrolling: 'touch',
                    px: 1.25,
                    py: 0.75,
                }}
            >
                {sectionOrder?.map((section, sectionIndex) => {
                    const sectionPages = groupedPages?.[section] || [];
                    if (sectionPages.length === 0) return null;

                    return (
                        <Box key={section} sx={{ mt: sectionIndex > 0 ? 1 : 0 }}>
                            <Typography
                                sx={{
                                    fontSize: '10px',
                                    fontWeight: 600,
                                    color: '#94a3b8',
                                    letterSpacing: '0.5px',
                                    textTransform: 'uppercase',
                                    px: 1,
                                    pt: sectionIndex > 0 ? 0.5 : 0,
                                    pb: 0.5,
                                    lineHeight: 1.3,
                                }}
                            >
                                {sectionLabels[section] || section}
                            </Typography>

                            {sectionPages.map((menu) => {
                                const isActive = activePage === menu?.pageName;
                                const MuiIcon = PAGE_MUI_ICONS[menu?.pageName];

                                return (
                                    <Box
                                        key={menu?.pageName || menu?.path}
                                        component={Link}
                                        to={getAdminPath(menu?.path)}
                                        onClick={() => onNavigate(menu?.path)}
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1.25,
                                            px: 1.25,
                                            py: '8px',
                                            minHeight: 40,
                                            borderRadius: '8px',
                                            textDecoration: 'none',
                                            color: isActive ? '#ffffff' : '#1e293b',
                                            backgroundColor: isActive ? '#3E4094' : 'transparent',
                                            mb: 0.5,
                                            '&:active': {
                                                backgroundColor: isActive ? '#363785' : '#f1f5f9',
                                            },
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                width: 20,
                                                height: 20,
                                                flexShrink: 0,
                                                color: isActive ? '#ffffff' : '#64748b',
                                            }}
                                        >
                                            {MuiIcon ? (
                                                <MuiIcon sx={{ fontSize: 18 }} />
                                            ) : (
                                                <Assets
                                                    src={getIconPath(menu)}
                                                    height="18px"
                                                    width="18px"
                                                />
                                            )}
                                        </Box>
                                        <Typography
                                            noWrap
                                            sx={{
                                                fontSize: '13px',
                                                fontWeight: isActive ? 600 : 500,
                                                lineHeight: 1.2,
                                                flex: 1,
                                                minWidth: 0,
                                            }}
                                        >
                                            {menu?.pageName}
                                        </Typography>
                                    </Box>
                                );
                            })}
                        </Box>
                    );
                })}
            </Box>

            <Box
                sx={{
                    flexShrink: 0,
                    borderTop: '1px solid #e2e8f0',
                    px: 1.25,
                    py: 1,
                    pb: 'calc(10px + env(safe-area-inset-bottom))',
                }}
            >
                <Box
                    role="button"
                    tabIndex={0}
                    onClick={onLogout}
                    onKeyDown={(e) => {
                        if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onLogout();
                        }
                    }}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.25,
                        px: 1.25,
                        py: '8px',
                        minHeight: 40,
                        borderRadius: '8px',
                        color: '#ef4444',
                        backgroundColor: '#fef2f2',
                        cursor: 'pointer',
                        '&:active': {
                            backgroundColor: '#fee2e2',
                        },
                    }}
                >
                    <LogoutOutlinedIcon sx={{ fontSize: 18 }} />
                    <Typography sx={{ fontSize: '13px', fontWeight: 600 }}>
                        Logout
                    </Typography>
                </Box>
            </Box>
        </Drawer>
    );
};

export default AdminMobileSidebar;
